import type {
  IntakeFormHouseholdMemberMapping,
  IntakeFormLayoutKey,
  IntakeFormStatus,
  IntakeFormSubmission,
} from "@/schema"
import { downloadBlob } from "@/utils/download-helpers"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { ApiRequestError } from "@/hooks/api/base"
import type { IntakeFileUploadInput, PublicIntakeFormDraftPayload } from "@/hooks/api/public-intake-forms"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import {
  cancelIntakeFormInvitationReminderSchedule,
  createIntakeForm,
  createIntakeFormApplication,
  createIntakeFormInvitation,
  createIntakeFormInvitationBatch,
  createIntakeFormTestInvitation,
  deleteIntakeFormSubmission,
  dismissIntakeFormSubmission,
  downloadIntakeFormCsvExport,
  downloadIntakeFormSubmissionConsentDocument,
  downloadIntakeFormSubmissionCsvExport,
  downloadIntakeFormSubmissionPdfExport,
  duplicateIntakeForm,
  installSharedIntakeForm,
  markIntakeFormSubmissionRead,
  restoreIntakeFormSubmission,
  revealIntakeFormSubmissionSensitiveAnswer,
  revokeIntakeFormInvitation,
  saveIntakeFormInvitationDraft,
  saveIntakeFormSubmissionConsentDocument,
  shareIntakeFormWithBook,
  submitIntakeFormFeedback,
  unshareIntakeFormFromBook,
  updateIntakeForm,
  updateIntakeFormContent,
  uploadIntakeFormInvitationDraftDocument,
  type CreateIntakeFormApplicationAttributes,
  type CreateIntakeFormAttributes,
  type CreateIntakeFormInvitationAttributes,
  type CreateIntakeFormInvitationBatchAttributes,
  type IntakeFormContentPath,
} from "./api"

export function useRevealIntakeFormSubmissionSensitiveAnswer(
  label: string,
  onReveal: (value: string) => void,
  onUnavailable: () => void
) {
  const getContext = useApiContext()
  return useMutation({
    mutationFn: async ({ submissionId, fieldKey }: { submissionId: string; fieldKey: string }) => {
      const response = await revealIntakeFormSubmissionSensitiveAnswer(await getContext(), submissionId, fieldKey)
      if (!response.value) throw new Error(`Unable to reveal ${label}`)
      // Keep plaintext only in the mounted answer, never in the mutation cache.
      onReveal(response.value)
    },
    gcTime: 0,
    meta: {
      errorToast: (error) =>
        error instanceof ApiRequestError && error.status === 404 ? null : `Unable to reveal ${label}`,
    },
    onError: (error) => {
      if (error instanceof ApiRequestError && error.status === 404) onUnavailable()
    },
  })
}

export function useCreateIntakeForm() {
  const getContext = useApiContext()
  const bookId = useBookId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (attributes: CreateIntakeFormAttributes) => createIntakeForm(await getContext(), attributes),
    onSuccess: (intakeForm) => {
      queryClient.setQueryData(["intake_form", bookId, intakeForm.id], intakeForm)
      queryClient.invalidateQueries({ queryKey: ["intake_forms"] })
    },
  })
}

export function useDuplicateIntakeForm() {
  const getContext = useApiContext()
  const bookId = useBookId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => duplicateIntakeForm(await getContext(), id),
    meta: { errorToast: { fallback: "Failed to duplicate form" } },
    onSuccess: (intakeForm) => {
      queryClient.setQueryData(["intake_form", bookId, intakeForm.id], intakeForm)
      queryClient.invalidateQueries({ queryKey: ["intake_forms"] })
    },
  })
}

export function useInstallSharedIntakeForm() {
  const getContext = useApiContext()
  const bookId = useBookId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ guid, schedulingEventTypeId }: { guid: string; schedulingEventTypeId?: string }) =>
      installSharedIntakeForm(await getContext(), guid, schedulingEventTypeId),
    onSuccess: (intakeForm) => {
      queryClient.setQueryData(["intake_form", bookId, intakeForm.id], intakeForm)
      queryClient.invalidateQueries({ queryKey: ["intake_forms"] })
    },
  })
}

export function useUpdateIntakeForm() {
  const getContext = useApiContext()
  const bookId = useBookId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...attributes
    }: {
      id: string
      name?: string
      status?: IntakeFormStatus
      layout_key?: IntakeFormLayoutKey
      field_mappings?: Record<string, string>
      household_member_mappings?: Record<string, IntakeFormHouseholdMemberMapping>
      notification_recipient_user_ids?: string[]
      default_email_subject?: string | null
      default_email_body?: string | null
    }) => updateIntakeForm(await getContext(), id, attributes),
    onSuccess: (intakeForm) => {
      queryClient.setQueryData(["intake_form", bookId, intakeForm.id], intakeForm)
      queryClient.invalidateQueries({ queryKey: ["intake_forms"] })
      queryClient.invalidateQueries({ queryKey: ["intake_form_preview", bookId, intakeForm.id] })
    },
  })
}

export function useUpdateIntakeFormContent() {
  const getContext = useApiContext()
  const bookId = useBookId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      editorRevision,
      path,
      value,
    }: {
      id: string
      editorRevision: number
      path: IntakeFormContentPath
      value: string
    }) => updateIntakeFormContent(await getContext(), id, { editor_revision: editorRevision, path, value }),
    onSuccess: (intakeForm) => {
      queryClient.setQueryData(["intake_form", bookId, intakeForm.id], intakeForm)
      queryClient.invalidateQueries({ queryKey: ["intake_forms"] })
      queryClient.invalidateQueries({ queryKey: ["intake_form_preview", bookId, intakeForm.id] })
    },
    meta: { errorToast: false },
  })
}

export function useShareIntakeFormWithBook() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ intakeFormId, bookId }: { intakeFormId: string; bookId: string }) =>
      shareIntakeFormWithBook(await getContext(), intakeFormId, bookId),
    onSuccess: (_bookIntakeForm, variables) => {
      queryClient.invalidateQueries({ queryKey: ["intake_form"] })
      queryClient.invalidateQueries({ queryKey: ["intake_forms"] })
      queryClient.invalidateQueries({ queryKey: ["intake_forms", "filter_options", variables.bookId] })
    },
    meta: { errorToast: { fallback: "Failed to share form" } },
  })
}

export function useUnshareIntakeFormFromBook() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ intakeFormId, bookIntakeFormId }: { intakeFormId: string; bookIntakeFormId: string }) =>
      unshareIntakeFormFromBook(await getContext(), intakeFormId, bookIntakeFormId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["intake_form"] })
      queryClient.invalidateQueries({ queryKey: ["intake_forms"] })
    },
    meta: { errorToast: { fallback: "Failed to remove form sharing" } },
  })
}

export function useSubmitIntakeFormFeedback() {
  const getContext = useApiContext()
  return useMutation({
    mutationFn: async ({ id, message }: { id: string; message: string }) =>
      submitIntakeFormFeedback(await getContext(), id, message),
    meta: { errorToast: { fallback: "Failed to send feedback. Please try again." } },
  })
}

export function useCreateIntakeFormInvitation() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (attributes: CreateIntakeFormInvitationAttributes) =>
      createIntakeFormInvitation(await getContext(), attributes),
    onSuccess: (invitation) => {
      queryClient.setQueryData(["intake_form_invitation", invitation.id], invitation)
      queryClient.invalidateQueries({ queryKey: ["intake_form_invitations"] })
      queryClient.invalidateQueries({ queryKey: ["intake_forms"] })
    },
  })
}

export function useCreateIntakeFormInvitationBatch() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (attributes: CreateIntakeFormInvitationBatchAttributes) =>
      createIntakeFormInvitationBatch(await getContext(), attributes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["intake_form_invitations"] })
      queryClient.invalidateQueries({ queryKey: ["intake_forms"] })
    },
  })
}

export function useCreateIntakeFormTestInvitation() {
  const getContext = useApiContext()
  return useMutation({
    mutationFn: async (intakeFormId: string) => createIntakeFormTestInvitation(await getContext(), intakeFormId),
  })
}

export function useRevokeIntakeFormInvitation() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => revokeIntakeFormInvitation(await getContext(), id),
    onSuccess: (_result, id) => {
      queryClient.invalidateQueries({ queryKey: ["intake_form_invitation", id] })
      queryClient.invalidateQueries({ queryKey: ["intake_form_invitations"] })
    },
  })
}

export function useSaveIntakeFormInvitationDraft() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, draft }: { id: string; draft: PublicIntakeFormDraftPayload }) =>
      saveIntakeFormInvitationDraft(await getContext(), id, draft),
    meta: { errorToast: false },
    onSuccess: (context, { id }) => {
      queryClient.setQueryData(["intake_form_invitation_draft", id], context)
      if (context.draft) {
        queryClient.invalidateQueries({ queryKey: ["intake_form_submission", context.draft.id] })
      }
      queryClient.invalidateQueries({ queryKey: ["intake_form_invitation", id] })
      queryClient.invalidateQueries({ queryKey: ["intake_form_invitations"] })
    },
  })
}

export function useUploadIntakeFormInvitationDraftDocument() {
  const getContext = useApiContext()
  return useMutation({
    mutationFn: async ({ id, ...input }: IntakeFileUploadInput & { id: string }) =>
      uploadIntakeFormInvitationDraftDocument(await getContext(), id, input),
    meta: { errorToast: false },
  })
}

export function useCancelIntakeFormInvitationReminderSchedule() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (reminderId: string) =>
      cancelIntakeFormInvitationReminderSchedule(await getContext(), reminderId),
    onSuccess: (invitation) => {
      queryClient.setQueryData(["intake_form_invitation", invitation.id], invitation)
      queryClient.invalidateQueries({ queryKey: ["intake_form_invitations"] })
    },
  })
}

export function useCreateIntakeFormApplication() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, attributes }: { id: string; attributes: CreateIntakeFormApplicationAttributes }) =>
      createIntakeFormApplication(await getContext(), id, attributes),
    onSuccess: (submission) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      if (submission.client) queryClient.invalidateQueries({ queryKey: ["client", submission.client.id] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
      queryClient.invalidateQueries({ queryKey: ["person"] })
      queryClient.invalidateQueries({ queryKey: ["people"] })
      queryClient.invalidateQueries({ queryKey: ["household"] })
      queryClient.invalidateQueries({ queryKey: ["households"] })
      queryClient.invalidateQueries({ queryKey: ["notes"] })
    },
    onSettled: (_submission, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ["intake_form_submission", variables.id] })
      queryClient.invalidateQueries({ queryKey: ["intake_form_submissions"] })
      queryClient.invalidateQueries({ queryKey: ["intake_form_invitations"] })
    },
  })
}

export function useMarkIntakeFormSubmissionRead() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => markIntakeFormSubmissionRead(await getContext(), id),
    meta: { errorToast: false },
    onSuccess: (_result, id) => {
      queryClient.setQueriesData(
        { queryKey: ["intake_form_submission", id] },
        (current: IntakeFormSubmission | undefined) =>
          current && !current.read_at ? { ...current, read_at: new Date().toISOString() } : current
      )
      queryClient.invalidateQueries({ queryKey: ["intake_form_submissions"] })
      queryClient.invalidateQueries({ queryKey: ["intake_form_invitations"] })
    },
  })
}

export function useDeleteIntakeFormSubmission() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => deleteIntakeFormSubmission(await getContext(), id),
    onSuccess: async (_result, id) => {
      queryClient.removeQueries({ queryKey: ["intake_form_submission", id] })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["intake_form_submissions"] }),
        queryClient.invalidateQueries({ queryKey: ["intake_form_invitations"] }),
        queryClient.invalidateQueries({ queryKey: ["intake_form"] }),
        queryClient.invalidateQueries({ queryKey: ["intake_forms"] }),
      ])
    },
  })
}

export function useDismissIntakeFormSubmission() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => dismissIntakeFormSubmission(await getContext(), id),
    onSuccess: async (_result, id) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["intake_form_submission", id] }),
        queryClient.invalidateQueries({ queryKey: ["intake_form_submissions"] }),
        queryClient.invalidateQueries({ queryKey: ["intake_form_invitations"] }),
        queryClient.invalidateQueries({ queryKey: ["intake_form"] }),
        queryClient.invalidateQueries({ queryKey: ["intake_forms"] }),
      ])
    },
  })
}

export function useRestoreIntakeFormSubmission() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => restoreIntakeFormSubmission(await getContext(), id),
    onSuccess: async (_result, id) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["intake_form_submission", id] }),
        queryClient.invalidateQueries({ queryKey: ["intake_form_submissions"] }),
        queryClient.invalidateQueries({ queryKey: ["intake_form_invitations"] }),
        queryClient.invalidateQueries({ queryKey: ["intake_form"] }),
        queryClient.invalidateQueries({ queryKey: ["intake_forms"] }),
      ])
    },
  })
}

export function useDownloadIntakeFormSubmissionConsentDocument() {
  const getContext = useApiContext()
  return useMutation({
    mutationFn: async ({ id, filename }: { id: string; filename: string }) => ({
      blob: await downloadIntakeFormSubmissionConsentDocument(await getContext(), id),
      filename,
    }),
    onSuccess: ({ blob, filename }) => downloadBlob(blob, filename),
    meta: { errorToast: { fallback: "Failed to download consent record" } },
  })
}

export function useDownloadIntakeFormSubmissionPdfExport() {
  const getContext = useApiContext()
  return useMutation({
    mutationFn: async ({ id, filename, fullForm }: { id: string; filename: string; fullForm?: boolean }) => ({
      blob: await downloadIntakeFormSubmissionPdfExport(await getContext(), id, fullForm),
      filename,
    }),
    onSuccess: ({ blob, filename }) => downloadBlob(blob, filename),
    meta: { errorToast: { fallback: "Failed to download submission PDF" } },
  })
}

export function useDownloadIntakeFormSubmissionCsvExport() {
  const getContext = useApiContext()
  return useMutation({
    mutationFn: async ({ id, filename }: { id: string; filename: string }) => ({
      blob: await downloadIntakeFormSubmissionCsvExport(await getContext(), id),
      filename,
    }),
    onSuccess: ({ blob, filename }) => downloadBlob(blob, filename),
    meta: { errorToast: { fallback: "Failed to download submission CSV" } },
  })
}

export function useDownloadIntakeFormCsvExport() {
  const getContext = useApiContext()
  return useMutation({
    mutationFn: async ({ id, filename }: { id: string; filename: string }) => ({
      blob: await downloadIntakeFormCsvExport(await getContext(), id),
      filename,
    }),
    onSuccess: ({ blob, filename }) => downloadBlob(blob, filename),
    meta: { errorToast: { fallback: "Failed to download responses CSV" } },
  })
}

export function useSaveIntakeFormSubmissionConsentDocument() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      storageFolderId,
      storageFolderName,
    }: {
      id: string
      storageFolderId?: string
      storageFolderName?: string
    }) =>
      saveIntakeFormSubmissionConsentDocument(await getContext(), id, {
        storage_folder_id: storageFolderId,
        storage_folder_name: storageFolderName,
      }),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["intake_form_submission", variables.id] })
      queryClient.invalidateQueries({ queryKey: ["household"] })
      queryClient.invalidateQueries({ queryKey: ["households"] })
    },
    meta: { errorToast: { fallback: "Failed to save consent record" } },
  })
}
